import React, { FC, useState } from 'react'
import { Field, InjectedFormProps, reduxForm } from 'redux-form'
import { Form, Collapse, Row, Col, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../../../reducers'

// components
import ValidationModal from '../../modals/ValidationModal'

// atoms
import InputField from '../../../atoms/InputField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import ColorPickerField from '../../../atoms/ColorPickerField'
import TextareaField from '../../../atoms/TextareaField'

// interfaces
import { IFormItemData } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT, RADIO_BTN_GROUP_FIELD } from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const ParagraphElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const { handleSubmit, onRemoveItem } = props
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.PARAGRAPH_ELEMENT_FORM]?.values)
	const formItemId = formValues?.i

	const fontSizes = [
		{ label: '12 px', value: '12px' },
		{ label: '14 px', value: '14px' },
		{ label: '16 px', value: '16px' },
		{ label: '18 px', value: '18px' },
		{ label: '20 px', value: '20px' }
	]

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Paragraph')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
				</Row>
				<Row className={'ml-4 mr-4 mb-4'} justify={'center'}>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Column in table')}
							name={ELEMENT_ATTRIBUTES.TABLE_COLUMN}
							getPopupContainer={(triggerNode: any) => triggerNode}
							options={columns?.data}
							size={'medium'}
							disabled={disable}
						/>
					</Col>
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Size')} key='1'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={disable}
									maxLength={3}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Text')} key='2'>
						<Field
							component={TextareaField}
							placeholder={t('loc:Enter text')}
							name={ELEMENT_ATTRIBUTES.LABEL}
							size={'medium'}
							disabled={disable}
							rows={6}
						/>
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
							<Col span={2} />
							<Col className={'h-34px'} span={11}>
								<Field
									component={SelectField}
									name={ELEMENT_ATTRIBUTES.FONT_SIZE}
									getPopupContainer={(triggerNode: any) => triggerNode}
									options={fontSizes}
									size={'medium'}
									disabled={disable}
									defaultValue={fontSizes[0]?.value}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col className={'h-34px'} span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_ALIGN}
									name={ELEMENT_ATTRIBUTES.TEXT_ALIGNMENT}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
							<Col span={2} />
							<Col className={'h-34px'} span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.LABEL_COLOR} size={'medium'} disabled={disable} />
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Validations')} key='3'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.PARAGRAPH_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.TEXT}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.PARAGRAPH_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(ParagraphElementForm)

export default form
